<script setup>
/**
 * What a connection is sent — one row per detail it declares.
 *
 * THE ROWS COME FROM THE CONNECTION, NOT FROM THE OWNER. A connection already declares its
 * details (`params` on the connection editor), so a free "add a detail" list here would be
 * a second place to misspell one, and a misspelling reaches the owner's own system as a
 * missing parameter rather than as an error anybody sees. When the connection is known,
 * its details are the rows; anything the task carries for a detail that no longer exists
 * is kept and shown as such, because deleting it silently is how a rename becomes data loss.
 *
 * EACH ROW IS AN ANSWER, A FIXED VALUE OR THE CHAT'S LANGUAGE, and that is a real choice rather
 * than "type a key or type a value in the same box": the sidecar's `InputSource` is a union,
 * and an owner who types `area` meaning the word rather than the saved answer gets a
 * connection called with the literal string `area` and no way to see why. The chat's language
 * (services field report F2.3) has nothing to type: Waslo sends "ar" or "en" for each chat.
 *
 * The saved-answer picker lists only keys saved EARLIER ON EVERY PATH to this step — the
 * same rule `validate.ts` enforces — so the commonest problem on this card cannot be
 * created here in the first place.
 *
 * FIELD REPORT F3.7. The owner saw only free boxes and could not find how to pass the studio
 * her customer picked. So:
 *   • a new row starts on "An answer collected earlier" whenever there is one to choose, and
 *     on a fixed value otherwise (the fixed value stays the second choice);
 *   • the answers arrive NAMED by the page ("The studio they picked (Pick a studio)");
 *   • an answer saved on only some ways to this step is named under the rows, so its absence
 *     from the list is explained (`someKeys`);
 *   • the one answer whose name means the same as the detail (`studio_id` and `studio`) is
 *     listed first and offered with "Use it". It is never filled without her press:
 *     `helpers/task-wiring.js` `suggestFor` says why.
 */
import { computed } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';

import WasloInput from './WasloInput.vue';
import WasloSelect from './WasloSelect.vue';
import { useWasloI18n } from '../i18n.js';
import { suggestFor } from '../helpers/task-wiring.js';

const props = defineProps({
  /** `[{ key, name, mode, from, value }]` — see `helpers/task-flow-draft.js`. */
  modelValue: { type: Array, default: () => [] },
  /** `[{ name, description, required }]` from the chosen connection, or null. */
  params: { type: Array, default: null },
  /** Chat keys saved before this step on every path — `[{ value, label }]`. */
  availableKeys: { type: Array, default: () => [] },
  /** F3.7: keys saved on only SOME ways to this step — `[{ value, label }]`. Never offered. */
  someKeys: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useWasloI18n();

const key = name => `WASLO.TASKS.EDITOR.FLOW.INPUTS.${name}`;

/** F3.7: an earlier answer when there is one to choose, otherwise a fixed value. */
const defaultMode = computed(() =>
  props.availableKeys.length > 0 ? 'key' : 'value'
);

/**
 * One row per declared detail, in the connection's order, then anything the task still
 * carries for a detail that is gone. `params === null` means we could not read the
 * connection — then the task's own rows are all there is, and they are shown as they are.
 */
const rows = computed(() => {
  if (props.params === null) return props.modelValue;
  const byName = new Map(props.modelValue.map(row => [row.name, row]));
  const declared = props.params.map(
    param =>
      byName.get(param.name) ?? {
        key: `declared-${param.name}`,
        name: param.name,
        mode: defaultMode.value,
        from: '',
        value: '',
      }
  );
  const names = new Set(props.params.map(param => param.name));
  return [...declared, ...props.modelValue.filter(row => !names.has(row.name))];
});

const isStale = name =>
  props.params !== null && !props.params.some(param => param.name === name);

const hint = name => {
  const param = (props.params ?? []).find(item => item.name === name);
  return param?.description ?? '';
};

const modeChoices = computed(() => [
  { value: 'key', label: t(key('MODE_ANSWER')) },
  { value: 'value', label: t(key('MODE_FIXED')) },
  { value: 'lang', label: t(key('MODE_LANG')) },
]);

const keyValues = computed(() =>
  props.availableKeys.map(option => option.value)
);

/** The answer whose name means the same as this detail, as an option, or null. */
const suggested = name => {
  const value = suggestFor(name, keyValues.value);
  return value === null
    ? null
    : (props.availableKeys.find(option => option.value === value) ?? null);
};

/** Shown only while the row is waiting for an answer to be chosen. */
const suggestionFor = row =>
  row.mode === 'key' && (row.from ?? '') === '' ? suggested(row.name) : null;

/** The suggested answer first, then the rest in graph order. */
const optionsFor = row => {
  const first = suggested(row.name);
  if (first === null) return props.availableKeys;
  return [
    first,
    ...props.availableKeys.filter(option => option.value !== first.value),
  ];
};

const someLine = computed(() =>
  props.someKeys.map(option => option.label).join(t(key('LIST_JOIN')))
);

/**
 * Rows are replaced wholesale, INCLUDING the ones with nothing in them yet.
 *
 * Dropping a blank row here would make the mode picker unusable: choosing "an answer
 * saved earlier" before picking which answer is the normal order, and a row that
 * disappeared the moment it was switched would take the picker with it. `inputsToWire`
 * is where a row with no source stops existing, which is also where the sidecar's schema
 * would have refused it.
 */
const write = next => emit('update:modelValue', next);

const setRow = (name, changes) => {
  const seen = rows.value.some(row => row.name === name);
  const next = seen
    ? rows.value.map(row => (row.name === name ? { ...row, ...changes } : row))
    : [
        ...rows.value,
        {
          key: `new-${name}`,
          name,
          mode: defaultMode.value,
          from: '',
          value: '',
          ...changes,
        },
      ];
  write(next);
};

const drop = name => write(rows.value.filter(row => row.name !== name));
</script>

<template>
  <div class="flex flex-col gap-3" data-waslo-step-inputs>
    <p class="mb-0 text-sm font-medium text-n-slate-12">
      {{ t(key('HEADING')) }}
    </p>

    <p
      v-if="params !== null && params.length === 0"
      class="mb-0 text-sm text-n-slate-11"
      data-waslo-step-inputs-none
    >
      {{ t(key('NONE')) }}
    </p>
    <p
      v-else-if="params === null"
      class="mb-0 text-sm text-n-slate-11"
      data-waslo-step-inputs-unknown
    >
      {{ t(key('UNKNOWN')) }}
    </p>

    <div
      v-for="row in rows"
      :key="row.key"
      class="flex flex-col gap-3 p-3 rounded-lg outline outline-1 outline-n-weak bg-n-solid-1"
      data-waslo-step-input
    >
      <div class="flex items-center justify-between gap-3">
        <p class="mb-0 text-sm font-medium text-n-slate-12">
          <bdi>{{ row.name }}</bdi>
        </p>
        <button
          v-if="isStale(row.name)"
          type="button"
          class="grid rounded-md place-items-center size-7 text-n-slate-11 hover:text-n-ruby-9 hover:bg-n-alpha-2"
          :aria-label="t(key('REMOVE'))"
          :title="t(key('REMOVE'))"
          data-waslo-step-input-remove
          @click="drop(row.name)"
        >
          <span class="size-4 i-lucide-trash-2" aria-hidden="true" />
        </button>
      </div>
      <p v-if="hint(row.name)" class="mb-0 text-sm text-n-slate-11">
        <bdi>{{ hint(row.name) }}</bdi>
      </p>
      <p
        v-if="isStale(row.name)"
        class="mb-0 text-sm text-n-amber-11"
        data-waslo-step-input-stale
      >
        {{ t(key('STALE')) }}
      </p>

      <div class="grid gap-3 md:grid-cols-2">
        <WasloSelect
          :model-value="row.mode"
          :label="t(key('MODE'))"
          :options="modeChoices"
          data-waslo-step-input-mode
          @update:model-value="value => setRow(row.name, { mode: value })"
        />
        <WasloSelect
          v-if="row.mode === 'key'"
          :model-value="row.from"
          :label="t(key('FROM'))"
          :options="optionsFor(row)"
          :placeholder="t(key('FROM_PLACEHOLDER'))"
          data-waslo-step-input-from
          @update:model-value="value => setRow(row.name, { from: value })"
        />
        <WasloInput
          v-else-if="row.mode === 'value'"
          :model-value="row.value"
          :label="t(key('FIXED'))"
          data-waslo-step-input-value
          @update:model-value="value => setRow(row.name, { value })"
        />
        <p
          v-else
          class="self-end mb-0 text-sm text-n-slate-11"
          data-waslo-step-input-lang
        >
          {{ t(key('LANG_NOTE')) }}
        </p>
      </div>

      <!-- F3.7: the answer that means the same, offered, never filled without her press. -->
      <div
        v-if="suggestionFor(row)"
        class="flex flex-wrap items-center gap-3"
        data-waslo-step-input-suggest
      >
        <p class="mb-0 text-sm text-n-slate-11">
          <bdi>{{
            t(key('SUGGEST'), { answer: suggestionFor(row).label })
          }}</bdi>
        </p>
        <Button
          :label="t(key('SUGGEST_USE'))"
          variant="faded"
          color="blue"
          size="sm"
          data-waslo-step-input-suggest-use
          @click="setRow(row.name, { from: suggestionFor(row).value })"
        />
      </div>
    </div>

    <p
      v-if="someKeys.length"
      class="mb-0 text-sm text-n-slate-11"
      data-waslo-step-inputs-some-paths
    >
      <bdi>{{ t(key('SOME_PATHS'), { answers: someLine }) }}</bdi>
    </p>

    <p
      v-if="availableKeys.length === 0 && someKeys.length === 0 && rows.length"
      class="mb-0 text-sm text-n-slate-11"
      data-waslo-step-inputs-no-keys
    >
      {{ t(key('NO_KEYS')) }}
    </p>

    <div v-if="params === null" class="flex">
      <Button
        :label="t(key('ADD'))"
        icon="i-lucide-plus"
        variant="faded"
        color="slate"
        size="sm"
        data-waslo-step-input-add
        @click="setRow(`detail_${rows.length + 1}`, {})"
      />
    </div>
  </div>
</template>
